// Composed-prompt builder for the Agent Skills editor.
// Editor spec §7 defines the prompt structure and rule-trigger handling.

import type { AgentFile, AgentFileNode } from "@/types/agent-skills";
import { evaluateRule } from "@/lib/agent-skills/triggers";
import type { EvalResult } from "@/lib/agent-skills/triggers";

export interface SectionStatus {
  /** The name of the agent/skill/subskill. */
  name: string;
  /** 'agent' has a neutral status; skills/subskills get rule-trigger evaluation. */
  kind: "agent" | "skill" | "subskill";
  /** Only set for rule-trigger skills; undefined for description-trigger or agent. */
  triggerResult?: EvalResult;
}

export interface ComposeArgs {
  /** The root agent node. */
  agent: AgentFile;
  /** All descendants in tree order (BFS/DFS as returned by useAgentFiles). */
  descendants: AgentFile[];
  /**
   * Parsed test context JSON object, or null if not provided / not parseable.
   * Used for rule-trigger badge evaluation only.
   */
  testContext: Record<string, unknown> | null;
}

export interface ComposeResult {
  /** The full composed system prompt text. */
  prompt: string;
  /** Per-section status for badge rendering in the UI. */
  sectionStatuses: SectionStatus[];
}

/**
 * Build the composed prompt and per-section statuses.
 *
 * Rules (editor spec §7):
 * 1. Agent body is emitted first, no header.
 * 2. Each skill: "## Skill: {name}\n{body}"
 * 3. Each sub-skill: "## Sub-skill: {name}\n{body}"
 * 4. Rule-trigger skills are evaluated against testContext and their result
 *    captured in sectionStatuses — but they are NEVER excluded from the prompt.
 * 5. Description-trigger and agents get a neutral/undefined triggerResult.
 */
export function composePrompt({ agent, descendants, testContext }: ComposeArgs): ComposeResult {
  const parts: string[] = [];
  const sectionStatuses: SectionStatus[] = [];

  // Agent body (no header)
  parts.push(agent.body.trim());
  sectionStatuses.push({ name: agent.name, kind: "agent" });

  // Descendants in tree order
  for (const desc of descendants) {
    const isSubskill = desc.kind === "subskill";
    const headerPrefix = isSubskill ? "## Sub-skill" : "## Skill";
    const header = `${headerPrefix}: ${desc.name}`;

    parts.push(`${header}\n${desc.body.trim()}`);

    // Evaluate rule triggers
    let triggerResult: EvalResult | undefined;
    if (desc.frontmatter.trigger_type === "rule" && desc.frontmatter.trigger_rule) {
      if (testContext !== null) {
        triggerResult = evaluateRule(desc.frontmatter.trigger_rule, testContext);
      } else {
        triggerResult = "no-context";
      }
    }

    sectionStatuses.push({
      name: desc.name,
      kind: desc.kind,
      triggerResult,
    });
  }

  const prompt = parts.join("\n\n");

  return { prompt, sectionStatuses };
}
